import React from "react";
import Mnemonic from "@/components/Mnemonic";

const FACES = [
  {
    name: "The Storyteller",
    body:
      "Warm, generous with knowledge, unhurried. Speaks in specifics — Sundari, golpata, hilsa, gawa — never in generalities. Every corner of the farm has a tale, and we tell it without performance.",
    is: "curious · specific · unhurried",
    never: "vague · theatrical · rushed",
  },
  {
    name: "The Farmer",
    body:
      "Grounded, patient, deeply knowledgeable. Understands the rhythm of seasons, the language of water, the patience of cultivation. Pride without boasting. Expertise without jargon.",
    is: "grounded · patient · expert",
    never: "boastful · technical · distant",
  },
  {
    name: "The Explorer",
    body:
      "Present, alive to discovery, adventurous within the familiar. Invites guests to look more closely — at a root, a tide, a flame. Wonder is not manufactured here; it is found.",
    is: "present · curious · inviting",
    never: "extreme · artificial · loud",
  },
  {
    name: "The Keeper",
    body:
      "Protective of culture, reverent of ecology, gentle in authority. Knows what must be preserved so the next generation can inherit it. Guests leave understanding that stewardship is a gift.",
    is: "protective · reverent · steady",
    never: "preachy · restrictive · solemn",
  },
];

export default function Story() {
  return (
    <div data-testid="page-story">
      {/* Intro */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <Mnemonic size={44} className="mb-8" style={{ opacity: 0.9 }} />
          <div
            className="italic-display mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "var(--sundari-gold)", opacity: 0.9 }}
            data-testid="page-bengali"
          >
            আমাদের কথা
          </div>
          <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Our Story</div>
          <h1 className="italic-display mt-6" style={{ fontSize: "clamp(52px, 8vw, 108px)", lineHeight: 1 }}>
            Not built.
            <br />
            <span style={{ color: "var(--sundari-gold)" }}>Grown.</span>
          </h1>
          <p className="mt-10 max-w-[720px]" style={{ fontSize: "17px", lineHeight: 1.75 }}>
            There are places that are built. And then there are places that are grown. FarmStory
            belongs to the latter. The name places cultivation, the fish pond, the crab trap, the
            aubergine plot at the centre — not in the background.
          </p>
        </div>
      </section>

      {/* Brand Story — verbatim */}
      <section
        className="hair-t"
        style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}
        data-testid="brand-story"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="eyebrow" style={{ color: "var(--sundari-gold)" }}>Brand Story</div>
            <h2 className="italic-display mt-6" style={{ fontSize: "clamp(34px, 4.4vw, 54px)", lineHeight: 1.02 }}>
              How the farm came to be.
            </h2>
            <hr className="divider-gold mt-8" />
            <p
              className="italic-display mt-8"
              style={{ fontSize: "18px", lineHeight: 1.6, opacity: 0.78 }}
            >
              A living ecosystem where agriculture, aquaculture, ecotourism, culture and technology
              exist in harmony.
            </p>
          </div>

          <div
            className="md:col-span-8 space-y-6"
            style={{ fontSize: "16.5px", lineHeight: 1.85, opacity: 0.92 }}
          >
            <p>
              FarmStory was born from a simple belief that the future of rural India lies not in
              replacing nature, but in growing alongside it. Nestled at the edge of the Sundarbans,
              where rivers meet mangrove forests and every tide reshapes the land, FarmStory is a
              living ecosystem where agriculture, aquaculture, ecotourism, culture and technology
              exist in harmony. It is neither a resort disguised as a farm nor a farm built for
              tourism. It is a place where every cottage stands lightly on the earth, every harvest
              tells the story of the land, and every experience reconnects people with nature and
              community.
            </p>
            <p>
              At FarmStory, innovation is rooted in purpose. Artificial intelligence, computer
              vision and data-driven technologies quietly support farming, water management,
              aquaculture, hospitality and conservation — allowing technology to strengthen human
              wisdom rather than replace it. Every solution is developed within a real-world
              environment, making FarmStory a living prototype for the future of sustainable rural
              enterprises.
            </p>
            <p>
              More than a destination, FarmStory is a movement to create resilient rural economies
              by generating livelihoods, celebrating local traditions and preserving fragile
              ecosystems. Farmers, artisans, women entrepreneurs and storytellers all become part
              of a shared journey where enterprise creates opportunity and sustainability becomes a
              way of life.
            </p>
            <p>
              As the first demonstration of a larger vision, FarmStory is laying the foundation for
              an AI-enabled rural enterprise platform that will connect agriculture, tourism, food
              processing, import-export and sustainable commerce across India, Thailand and the
              wider ASEAN region.
            </p>
            <p
              className="italic-display pt-2"
              style={{
                fontSize: "clamp(19px, 2vw, 24px)",
                lineHeight: 1.5,
                color: "var(--sundari-gold)",
              }}
            >
              Every seed planted, every home built and every story shared is a reminder that the
              most enduring futures are not constructed overnight, but cultivated with patience,
              purpose and respect for the land.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="hair-t hair-b" style={{ background: "var(--bamboo-cream)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-3 gap-10 md:gap-16">
          {[
            {
              label: "Mission",
              text:
                "To nurture a living sanctuary at the edge of the world's greatest mangrove delta.",
            },
            {
              label: "Vision",
              text:
                "To become India's most beloved agri-ecological retreat, a place where the delta's wisdom is preserved, practised, and shared with the world.",
            },
            {
              label: "Values",
              text:
                "Rootedness · Regeneration · Storytelling · Simplicity · Community — five words that decide what stays and what does not.",
            },
          ].map((b) => (
            <div key={b.label} className="hair-t pt-8" data-testid={`mvv-${b.label.toLowerCase()}`}>
              <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>{b.label}</div>
              <p
                className="mt-5"
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "22px",
                  lineHeight: 1.4,
                  color: "var(--mangrove-night)",
                }}
              >
                {b.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Sage Caregiver banner + Four Faces */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="text-center max-w-[820px] mx-auto">
            <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>
              The Sage Caregiver — Brand Archetype
            </div>
            <h2 className="italic-display mt-5" style={{ fontSize: "clamp(34px, 4.4vw, 56px)", lineHeight: 1.05 }}>
              Ancient knowledge · generous hospitality · groundedness · living wisdom
            </h2>
            <hr className="divider-gold mx-auto mt-8" />
            <p className="mt-8 label-track" style={{ color: "var(--delta-clay)" }}>
              The Four Faces of FarmStory
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FACES.map((f, i) => (
              <article
                key={f.name}
                className="hair-all rounded-[8px] p-6 md:p-7"
                style={{ background: "var(--bamboo-cream)" }}
                data-testid={`face-${f.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="label-track" style={{ color: "var(--sundari-gold)" }}>0{i + 1}</div>
                <h3 className="italic-display mt-3" style={{ fontSize: "28px", lineHeight: 1.05 }}>
                  {f.name}
                </h3>
                <p className="mt-4" style={{ fontSize: "13.5px", lineHeight: 1.7, color: "var(--mangrove-night)" }}>
                  {f.body}
                </p>
                <div className="mt-6 hair-t pt-4">
                  <div className="label-track" style={{ color: "var(--golpata-green)" }}>Is</div>
                  <p className="mt-1 text-[13px]">{f.is}</p>
                </div>
                <div className="mt-3">
                  <div className="label-track" style={{ color: "var(--dusk-coral)" }}>Never</div>
                  <p className="mt-1 text-[13px]">{f.never}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
